import {
  PO_OVERRIDE_COOKIE_MAX_AGE,
  PO_OVERRIDE_COOKIE_NAME,
} from "~/stores/user";

const RETURN_PATH_QUERY_KEYS = ["returnTo", "callback-url", "redirectTo"];

function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function decodeReturnPath(value) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
}

// A same-site path: one leading slash, not followed by "/" or "\" (which would
// make it protocol-relative, i.e. another host).
function isInternalPath(path) {
  return /^\/(?![/\\])/.test(path);
}

function isLoginRoute(path, router) {
  const routeName = router.resolve(path).route?.name ?? "";
  return routeName.split("___")[0] === "login";
}

/**
 * The page the login page was asked to return to, if it is a safe target:
 * an internal path that is not the login page itself.
 *
 * @param {object} query - the login route's query
 * @param {object} router - the app's vue-router instance
 * @returns {string | null}
 */
export function requestedReturnPath(query, router) {
  for (const key of RETURN_PATH_QUERY_KEYS) {
    const value = firstQueryValue(query[key]);
    if (typeof value !== "string" || !value) {
      continue;
    }
    const path = decodeReturnPath(value);
    if (isInternalPath(path) && !isLoginRoute(path, router)) {
      return path;
    }
  }
  return null;
}

/**
 * Where a user lands after logging in when no return path was requested.
 *
 * @param {object} params
 * @param {object} params.query - the login route's query
 * @param {object | null} params.me - the logged-in user
 * @param {Function} params.localePath
 * @returns {string}
 */
export function defaultPostLoginPath({ query, me, localePath }) {
  if (query.from === "po") {
    return localePath({ name: "myPriceOffersIndexPage" });
  }
  if (me?.isCustomer) {
    return localePath({ name: "myTenders" });
  }
  if (me?.isMate) {
    return localePath({ name: "buyTendersPage" });
  }
  if (me?.isPriceOfferProfessional) {
    return localePath({ name: "myPriceOffersIndexPage" });
  }
  return localePath({ name: "indexPage" });
}

/**
 * The page to send a user to after login, or when an already logged-in user
 * opens the login page. Never the login page itself.
 *
 * @param {object} params
 * @param {object} params.query - the login route's query
 * @param {object | null} params.me - the logged-in user
 * @param {Function} params.localePath
 * @param {object} params.router - the app's vue-router instance
 * @returns {string}
 */
export function resolvePostLoginPath({ query, me, localePath, router }) {
  return (
    requestedReturnPath(query, router) ??
    defaultPostLoginPath({ query, me, localePath })
  );
}

/**
 * A professional logging in from the price-offer landing (`?from=po`) uses the
 * price-offer creator as a price-offer professional.
 *
 * @param {object} params
 * @param {object} params.query - the login route's query
 * @param {object} params.userStore
 * @param {object} params.$cookie
 * @param {object} params.$helpers
 */
export function applyPriceOfferLoginOverride({
  query,
  userStore,
  $cookie,
  $helpers,
}) {
  if (query.from !== "po" || userStore.me?.isMate !== true) {
    return;
  }
  $cookie.set(PO_OVERRIDE_COOKIE_NAME, String(userStore.me.id), {
    maxAge: PO_OVERRIDE_COOKIE_MAX_AGE,
    domain: $helpers.getMainDomain(),
    path: "/",
  });
  userStore.applyPriceOfferOverride();
}
